import { type ReactNode } from 'react';
import { UserSubscriptionType } from '../services/subscriptions/subscriptions.types';
import { UserPlanType } from '../hooks/useUserSubscription/useUserSubscription.types';
import type { Entitlement } from '@frontegg/nextjs';

export type UserEntitlementsContextType = {
  plan: UserSubscriptionType | undefined;
  isPaidUser: boolean;
  userPlanType: UserPlanType | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isUpgradeButtonVisible: boolean;
  hasPaidSubscription: boolean;
  isTrustedCustomer: boolean;
  useFeatureEntitlements: (featureKey: FronteggFeatureKeys) => Entitlement;
  usePermissionEntitlements: (
    permissionKey: FronteggPermissionKeys,
  ) => Entitlement;
  usePatientFeatureEntitlements: (
    featureKey: FronteggFeatureKeys,
  ) => Entitlement;
  usePatientPermissionEntitlements: (
    permissionKey: FronteggPermissionKeys,
  ) => Entitlement;
};

export enum FronteggFeatureKeys {
  ProPlan = 'pro-plan-feature', // DEPRECATED - use specific entitlement checks instead
  PremiumPlan = 'premium-feature', // DEPRECATED - use specific entitlement checks instead
  DayPass = 'day-pass-feature',
  SubscriptionManagement = 'subscription-management-feature',
  TrustedCustomer = 'trusted-customer-feature',
  CustomRoomInvite = 'custom-room-invite-feature',
  ProPlanSubscription = 'pro-plan-subscription-feature', // DEPRECATED - use specific entitlement checks instead
  Passcode = 'passcode-feature',
  Branding = 'branding-feature',
  PatientTransfer = 'patient-transfer-feature',
  UseNativeRooms = 'use-native-rooms',
  CanSharePersonalRoom = 'share-personal-rooms-feature',
  CreateSharedRooms = 'create-shared-rooms-feature',
  SharedRoomsAnalytics = 'shared-rooms-analytics-feature',
  SharedRoomsHistory = 'shared-rooms-history-feature',
  InviteUser = 'invite-users-feature',
  InviteUsersWithAccountCreation = 'invite-with-account-creation-feature',
  ExportUsers = 'export-users-feature',
  TenantBaaManagement = 'tenant-baa-management',
  CustomWaitingRoom = 'custom-waiting-room-feature',
  // DO NOT USE THIS. It is to support backwards compatibility with the isPaid check in v2. Use specific feature checks instead
  LegacyIsPaid = 'legacy-ispaid-feature',
}

export enum FronteggPermissionKeys {
  CancelSubscription = 'cancel-subscription-permission',
  UpdateSubscription = 'update-subscription',
  ViewBillingPortal = 'view-billing-portal',
  ViewSubscriptionTab = 'view-subscription-tab',
  UpdateTenantSettings = 'update-tenant-settings',
  CanManageApps = 'can-manage-apps',
  AnalyticsAllRooms = 'analytics-all-rooms-permission',
  HistoryAllRooms = 'history-all-rooms-permission',
  ViewSeatsBanner = 'view-seats-banner',
  ManageTenantRooms = 'manage-tenant-rooms',
  ManageBranding = 'manage-branding',
  CreateTenantBaa = 'create-tenant-baa',
  DeleteTenantBaa = 'delete-tenant-baa',
}

export type InternalProviderProps = {
  children: ReactNode;
};

export type UserEntitlementsProviderProps = InternalProviderProps & {
  isAuthenticated: boolean;
};
